// import Vue from 'vue'
import Api from '@/api/interview.js';

const state = {
  loading: false,
  error: null,
  videointerview: null,
  questionIndex: null,
  recording: false,
  uploading: false,
  uploadProgress: null,
  testVideo: null,
  paginatorLeftBoundIndex: 0,
  isStartOrEndPage: true,
  is_video_started: false,
  first_click_was: false,
  is_finish_test: false,
  isSubmitEnabledProp: false,
  streamIds: {},
  devices: [],
  //Variables to use in the quick scan
  quickScan: [
    {
      label: '4K(UHD)',
      width: 3840,
      height: 2160,
      ratio: '16:9'
    },
    {
      label: '1080p(FHD)',
      width: 1920,
      height: 1080,
      ratio: '16:9'
    },
    {
      label: 'UXGA',
      width: 1600,
      height: 1200,
      ratio: '4:3'
    },
    {
      label: '720p(HD)',
      width: 1280,
      height: 720,
      ratio: '16:9'
    },
    {
      label: 'SVGA',
      width: 800,
      height: 600,
      ratio: '4:3'
    },
    {
      label: 'VGA',
      width: 640,
      height: 480,
      ratio: '4:3'
    },
    {
      label: '360p(nHD)',
      width: 640,
      height: 360,
      ratio: '16:9'
    },
    {
      label: 'CIF',
      width: 352,
      height: 288,
      ratio: '4:3'
    },
    {
      label: 'QVGA',
      width: 320,
      height: 240,
      ratio: '4:3'
    },
    {
      label: 'QCIF',
      width: 176,
      height: 144,
      ratio: '4:3'
    },
    {
      label: 'QQVGA',
      width: 160,
      height: 120,
      ratio: '4:3'
    }
  ],
  enabledResolutions: []
};

const actions = {
  fetch({ commit }, { interview }) {
    if (!interview || !interview.token) {
      throw new Error('Empty interview token');
    }

    commit('fetch');

    return Api.fetchOneVideointerview(interview.token)
      .then(data => {
        commit('fetchSuccess', data);

        return data;
      })
      .catch(err => {
        commit('fetchFailure', err);

        return Promise.reject(err);
      });
  },

  end({ commit, dispatch }, { interview }) {
    commit('end');

    return Api.endOneVideointerview(interview.token)
      .then(() => {
        commit('endSuccess');
        dispatch('interview/fetch', { token: interview.token }, { root: true });
      })
      .catch(err => {
        commit('saveFailure', err);

        return Promise.reject(err);
      });
  },

  setCurrentQuestion({ commit, state, getters, dispatch }, { interview, questionIndex }) {
    if (!interview || !interview.token) {
      throw new Error('Empty interview token');
    }

    if (state.recording) {
      throw new Error('Answer in progress');
    }

    if (!getters.questions[questionIndex]) {
      throw new Error(`Invalid question index ${questionIndex}`);
    }

    commit('setQuestionIndex', questionIndex);
    commit('setRecording', false);
    commit('setUploading', false);
    commit('setUploadProgress', null);

    const question = getters['currentQuestion'];

    if (!question.is_shown && question.preparation_time === 0) {
      dispatch('showQuestion', { interview });
    }
  },

  showQuestion({ commit, getters }, { interview }) {
    if (!interview || !interview.token) {
      throw new Error('Empty interview token');
    }

    let question = getters['currentQuestion'];

    return Api.showOneVideointerviewQuestion(interview.token, question.id)
      .then(data => {
        question = data;
      })
      .then(() => {
        return Api.fetchOneVideointerview(interview.token);
      })
      .then(data => {
        commit('fetchSuccess', data);
      })
      .then(() => {
        return question.preparation_time;
      })
      .catch(err => {
        commit('fetchFailure', err);
        return Promise.reject(err);
      });
  },

  showVideo({ commit, getters }, { interview }) {
    if (!interview || !interview.token) {
      throw new Error('Empty interview token');
    }

    let question = getters['currentQuestion'];

    return Api.showOneVideointerviewVideo(interview.token, question.id)
      .then(() => {
        return Api.fetchOneVideointerview(interview.token);
      })
      .then(data => {
        commit('fetchSuccess', data);
      })
      .catch(err => {
        commit('fetchFailure', err);
        return Promise.reject(err);
      });
  },

  skipQuestion({ commit, getters }, { interview }) {
    if (!interview || !interview.token) {
      throw new Error('Empty interview token');
    }

    let question = getters['currentQuestion'];

    if (question.is_skipped) {
      throw new Error('Question is already skipped');
    }

    if (question.is_required) {
      throw new Error('Question is mandatory');
    }

    return Api.skipOneVideointerviewQuestion(interview.token, question.id)
      .then(data => {
        question = data;
      })
      .then(() => {
        return Api.fetchOneVideointerview(interview.token);
      })
      .then(data => {
        commit('fetchSuccess', data);
      })
      .catch(err => commit('fetchFailure', err));
  },

  startRecording({ commit, getters }, { interview }) {
    let question = getters['currentQuestion'];

    if (!question) {
      throw Error('Current question is empty');
    }

    commit('setRecording', true);

    return Api.startOneVideointerviewQuestion(interview.token, question.id);
  },

  stopRecording({ commit, getters, dispatch }, { interview, blob }) {
    commit('setRecording', false);
    // console.log('stopRecording blob: ', blob);

    if (!interview || !interview.token) {
      throw new Error('Empty interview token');
    }

    let question = getters['currentQuestion'];
    // console.log('stopRecording question: ', question);

    if (!question) {
      throw Error('Current question is empty');
    }

    return Api.saveOneVideointerviewQuestion(interview.token, question.id, blob, ({ completed }) =>
      commit('setUploadProgress', completed)
    )
      .then(() => {
        return dispatch('fetch', { interview });
      })
      .catch(err => {
        commit('saveFailure', err);
        return Promise.reject(err);
      });
  },

  async stopTestRecording({ commit }, { interview, blob }) {
    return await Api.saveOneVideointerviewTestVideo(interview.token, blob, ({ completed }) =>
      commit('setUploadProgress', completed)
    ).then(data => {
      commit('setTestVideo', data);
    });
  }
};

const mutations = {
  fetch(state) {
    state.error = null;
    state.loading = true;
  },

  fetchSuccess(state, data) {
    // const videos = [
    //   'https://my.vcv.ru/storage/video/917a6de729bfa9b2f00925f258a80c1f.mp4?1597132631',
    //   'https://my.vcv.ru/storage/video/917a6de729bfa9b2f00925f258a80c1f.mp4?1597132631',
    //   'https://my.vcv.ru/storage/video/917a6de729bfa9b2f00925f258a80c1f.mp4?1597132631',
    //   'https://my.vcv.ru/storage/video/917a6de729bfa9b2f00925f258a80c1f.mp4?1597132631'
    // ];
    // const questions = data.questions.map((item, index) => {
    //   item.question_media.question_video.source = 'https://my.vcv.ru/storage/video/917a6de729bfa9b2f00925f258a80c1f.mp4?1597132631'
    //   item.question_media.question_video.preview = 'https://my.vcv.ru/storage/video-preview/5437971f65dd5673c1f3f3a307542fad.jpg?1597132592'
    //   return Object.assign({}, item, {
    //     question_video: videos[index] || 'https://my.vcv.ru/storage/video/917a6de729bfa9b2f00925f258a80c1f.mp4?1597132631',
    //     is_youtube: false
    //   })
    // })
    // const new_data = Object.assign({}, data, {
    //   questions
    // });
    state.videointerview = data;
    if (data && data.questions) {
      let streamIds = {};
      data.questions.forEach(item => {
        if (item.video && item.video.stream_recording_name) {
          streamIds = Object.assign({}, streamIds, {
            [item.id]: item.video.stream_recording_name
          });
        }
      });
      state.streamIds = Object.assign({}, state.streamIds, streamIds);
    }
    state.loading = false;
  },

  fetchFailure(state, err) {
    state.error = err;
    state.loading = false;
  },

  saveFailure(state, err) {
    state.error = err;
    state.loading = false;
    console.error(err);
  },

  end(state) {
    state.loading = true;
  },

  endSuccess(state) {
    state.loading = false;
  },

  setQuestionIndex(state, index) {
    if (state.recording) {
      throw new Error('Answer in progress');
    }
    state.questionIndex = index;
  },

  setRecording(state, value) {
    state.recording = value;
  },

  setUploading(state, value) {
    state.uploading = value;
  },

  setUploadProgress(state, value) {
    state.uploadProgress = !isNaN(value) ? value : 0;

    if (value === 100 || !isNaN(value)) {
      state.uploading = false;
    }
  },

  setTestVideo(state, video) {
    state.testVideo = video;
  },

  setPaginatorLeftBoundIndex(state, index) {
    state.paginatorLeftBoundIndex = index;
  },

  setSubmitEnabledProp(state, v) {
    state.isSubmitEnabledProp = v;
  },

  isStartOrEndPageChange(state, value) {
    state.isStartOrEndPage = value;
  },

  setEnabledResolutions(state, value) {
    console.log('value: ', value);
    console.log('state.enabledResolutions 1: ', state.enabledResolutions);
    state.enabledResolutions = [...state.enabledResolutions, value];
    console.log('state.enabledResolutions 2: ', state.enabledResolutions);
  },

  setDevices(state, value) {
    // console.log('value: ', value)
    // console.log('state.devices 1: ', state.devices)
    state.devices = value;
    // console.log('state.devices 2: ', state.devices)
  },

  clearEnabledResolutions(state) {
    state.enabledResolutions = [];
  },

  next_stage(state) {
    state.is_finish_test = true;
  },

  toggle_start_video(state, value) {
    state.is_video_started = value;
  },

  set_first_click_was(state, value) {
    state.first_click_was = value;
  },

  clear(state) {
    state.is_video_started = false;
  }
};

const getters = {
  // error(state) {
  //   return state.error;
  // },

  loading(state) {
    return state.loading;
  },

  one(state) {
    return state.videointerview || {};
  },

  questions(state) {
    return state.videointerview ? state.videointerview.questions : [];
  },

  questionIndex(state) {
    return state.questionIndex;
  },

  currentQuestion(state, getters) {
    return getters['questions'][state.questionIndex] || null;
  },

  isVideoAnswerEnabled(state, getters) {
    return getters['questions'][0] && getters['questions'][0].is_video_answer_enabled;
  },

  testVideo(state) {
    return state.testVideo;
  },

  // recording(state) {
  //   return state.recording;
  // },

  // uploadProgress (state, getters) {
  //   return state.uploadProgress
  // },

  isStartOrEndPage(state) {
    return state.isStartOrEndPage;
  },

  is_video_started(state) {
    return state.is_video_started;
  },

  first_click_was(state) {
    return state.first_click_was;
  },

  is_finish_test(state) {
    return state.is_finish_test;
  },

  isSubmitEnabledProp(state) {
    return state.isSubmitEnabledProp;
  },

  // streamIds(state) {
  //   return state.streamIds;
  // },

  quickScan(state) {
    return state.quickScan;
  },

  // devices(state, getters) {
  //   return state.devices
  // },

  // enabledResolutions(state, getters) {
  //   return state.enabledResolutions
  // },

  paginator_left_bound_index(state) {
    return state.paginatorLeftBoundIndex;
  }
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
